import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api, setToken } from './api';
import { isSectionAllowed as isSectionAllowedRule } from './sectionCatalog';

export type Role = 'user' | 'manager' | 'admin';

/** Per-user IMS Containers access toggles. See backend User.containerAccess.
 *  Optional on the frontend type so legacy session payloads (pre-feature)
 *  still type-check; helpers below treat missing as all-defaults-false. */
export interface ContainerAccess {
  canChangeStatus?: boolean;
  hidePricing?: boolean;
  hideProcurement?: boolean;
  hideVendor?: boolean;
  hideClientPii?: boolean;
  hideContractDocs?: boolean;
  hiddenSubpages?: string[];
  /** Per-section grants (e.g. `ims:overview/kpi:total`). See
   *  lib/sectionCatalog.ts for the catalog + isSectionAllowed semantics. */
  sectionGrants?: string[] | null;
  /** Container status families this user may flip "Ready" in Live Stock
   *  (e.g. ['STOCK','DEPLOYED']). Admin/master → all. See utils/mark-ready. */
  markReadyStatuses?: string[] | null;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: Role;
  /** Catalog keys this user is allowed to see (Layout sidebar + route
   *  guards). null = legacy "full access" sentinel; [] = explicit deny-all.
   *  Admins ignore this field — they always have full access. */
  allowedPages?: string[] | null;
  /** Spares-side product views the account may use ('spares'/'service').
   *  null/undefined = both (legacy). Domain-scoped page visibility. */
  productAccess?: string[] | null;
  /** Dedicated IMS Containers account — walled off from Spares routes. */
  imsOnly?: boolean;
  /** Master/super admin — sees every stage's pending work on the dashboard. */
  master?: boolean;
  /** When true (or admin), the Edit button appears on existing Ticket
   *  Requests inside Travel Planning → Ticket Request tab. */
  canEditTickets?: boolean;
  /** The Petty Cash custodian — can record/submit petty-cash expenses. */
  pettyCashCustodian?: boolean;
  /** Accounts/Finance team — may accept/reject money flows (sanction/reimburse
   *  petty cash, finalize/pay travel settlements) without being an admin. */
  accountsTeam?: boolean;
  /** Restricted technician login — when set, the account only sees its own
   *  technician data, tech entries, and pending Stage-6 forms. */
  technicianId?: string | null;
  technicianName?: string;
  /** Per-user IMS Containers access toggles (see ContainerAccess). */
  containerAccess?: ContainerAccess | null;
  /** Procurement Portal realm. When set (non-null), the account is walled
   *  to /procurement* paths only — mirrors the existing imsOnly / technicianId
   *  realm pattern. The value is the sub-role gating which procurement
   *  screens / actions render. */
  procurementRole?: ProcurementRole | null;
  /** Porta Cabin realm (porta.crystalgrp.xyz). When set, walled to /porta*.
   *  v2.0 Order Tracking adds six functional roles (spec Section 7) alongside
   *  the legacy admin/manager/operator. Must stay in lockstep with PORTA_ROLES
   *  in backend/src/models/User.ts. */
  portaRole?: PortaRole | null;
  /** Cross-realm access grant — realms this single account may enter beyond its
   *  walled primary (see backend User.realmAccess). Non-empty enables the in-app
   *  Switch button + relaxes the App.tsx realm guard. e.g. ['procurement','ims']
   *  for a purchase account that can also open IMS Containers. */
  realmAccess?: string[];
  /** Set when the super-admin is "viewing as" this account. Names the real
   *  admin behind the session so the app can show the exit banner. Null/absent
   *  on every normal session. */
  impersonatedBy?: { id: string; email: string } | null;
}

/** The single identity allowed to impersonate (mirrors the backend gate). */
export const SUPER_ADMIN_EMAIL = 'aa@crystalgroup.in';

/** True when THIS session may start an impersonation (the super-admin, and not
 *  already inside one). */
export function canImpersonate(user: User | null | undefined): boolean {
  return !!user && String(user.email || '').toLowerCase() === SUPER_ADMIN_EMAIL && !user.impersonatedBy;
}

/** Procurement Portal sub-roles — must stay in lockstep with PROCUREMENT_ROLES
 *  in backend/src/models/User.ts. The role drives sidebar visibility and
 *  per-page action gating; 'admin' is implicitly allowed everywhere. */
export type ProcurementRole = 'admin' | 'purchaser' | 'hod' | 'accounts' | 'logistics' | 'depot' | 'vendor';

/** Porta Cabin realm sub-roles — must stay in lockstep with PORTA_ROLES in
 *  backend/src/models/User.ts. Legacy admin/manager/operator + the v2.0 Order
 *  Tracking functional roles. */
export type PortaRole = 'admin' | 'manager' | 'operator'
  | 'ops' | 'procurement' | 'qc_inspector' | 'procurement_head' | 'logistics' | 'management';

// ----- IMS sub-page hide predicates ---------------------------------------
// Mirrors backend/src/services/container-acl.ts. Admins are always
// permissive; everyone else applies the persisted toggles.

const CATEGORY_HIDDEN_SUBPAGES: Record<string, keyof ContainerAccess> = {
  'ims:vendors':     'hideVendor',
  'ims:procurement': 'hideProcurement',
  'ims:financials':  'hidePricing',
};

/** True when the user can see the named IMS sub-page (catalog key). */
export function isContainerSubpageAllowed(user: User | null | undefined, pageKey: string): boolean {
  if (!user) return false;
  if (user.role === 'admin') return true;
  const access = user.containerAccess || {};
  if (Array.isArray(access.hiddenSubpages) && access.hiddenSubpages.includes(pageKey)) return false;
  const flag = CATEGORY_HIDDEN_SUBPAGES[pageKey];
  if (flag && (access as any)[flag] === true) return false;
  return true;
}

/** True when the user can change container statuses in-app. */
export function canChangeContainerStatus(user: User | null | undefined): boolean {
  if (!user) return false;
  if (user.role === 'admin') return true;
  return user.containerAccess?.canChangeStatus === true;
}

/** Hook flavour of isSectionAllowed for IMS UI gates. */
export function useSectionAllowed(sectionId: string): boolean {
  const { user } = useAuth();
  return isSectionAllowedRule(user as any, sectionId);
}

interface AuthCtx {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, product?: 'containers' | 'spares' | 'service' | 'procurement' | 'porta') => Promise<void>;
  logout: () => Promise<void>;
  /** Super-admin only — step into another account without its password. Pass a
   *  user id or an employee code. Replaces the current session with the target's
   *  (a persistent banner + exit remains via `impersonatedBy`). */
  impersonate: (target: { userId?: string; employeeCode?: string }) => Promise<void>;
  /** Return from an impersonation to the real admin account. */
  stopImpersonating: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // React Query client — used to nuke the cache on login + logout so a
  // previous user's response data (e.g. unmasked /analytics from when an
  // admin was logged in) can't leak into the next user's session.
  // Without this the new user sees the old user's data for a brief moment
  // because the 10s staleTime serves cached data while a refetch runs.
  const qc = useQueryClient();

  useEffect(() => {
    api
      .get<{ user: User }>('/api/auth/me')
      .then((r) => setUser(r.user))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  // Listen for `auth:expired` events fired from the api wrapper on 401 so
  // we drop the React user state in sync with the localStorage token clear.
  // Without this the Protected route kept rendering with a stale user and
  // every subsequent request silently 401'd, leaving mutations stuck on
  // "Saving…" until the user hard-refreshed the page.
  useEffect(() => {
    const handler = () => setUser(null);
    window.addEventListener('auth:expired', handler);
    return () => window.removeEventListener('auth:expired', handler);
  }, []);

  async function login(email: string, password: string, product?: 'containers' | 'spares' | 'service' | 'procurement' | 'porta') {
    // Backend returns { token, user } — store the token so iOS (ITP) can use
    // a Bearer header when the cross-origin cookie is silently dropped.
    // `product` tells the backend which login tab was clicked so the
    // wrong-tab realm guard works for IMS users with real email domains.
    const res = await api.post<{ token: string; user: User }>('/api/auth/login', { email, password, product });
    if (res.token) setToken(res.token);
    // Clear EVERY cached query before publishing the new user. Stops
    // unmasked responses from the previous session showing up to the
    // newcomer for the brief refetch window.
    qc.removeQueries();
    // Belt-and-suspenders: fetch /me to GUARANTEE the user state has the
    // full containerAccess (including sectionGrants) before any page
    // renders. The login response should contain everything, but /me is
    // built by the auth middleware which is the canonical shape — so we
    // trust it as the source of truth. Without this, a stale or
    // partial login response leaves the first render in "default
    // permissive" mode (no grants → everything shows) and the user has
    // to refresh.
    let userToPublish: User = res.user;
    try {
      const meRes = await api.get<{ user: User }>('/api/auth/me');
      if (meRes?.user) userToPublish = meRes.user;
    } catch {
      // /me failed — fall back to the login response. We still publish
      // so the user isn't stuck on the login screen, but the first
      // render may briefly mis-gate sections until the next /me call.
    }
    setUser(userToPublish);
  }

  async function logout() {
    await api.post('/api/auth/logout').catch(() => {});
    setToken(null);
    // Drop the product-view scope set at login so the next login (or the dev
    // tab picker on an unscoped host) starts clean. On a real product subdomain
    // currentProduct() re-derives the same value from the hostname anyway.
    try { sessionStorage.removeItem('cg-product'); } catch { /* ignore */ }
    // Same as on login — drop everything so the next visitor doesn't
    // inherit this user's cached pages.
    qc.removeQueries();
    setUser(null);
  }

  // Swap the session to `res.token`/`res.user` — shared by impersonate + exit.
  // Mirrors login(): store token, nuke the query cache so the previous
  // identity's cached (possibly unmasked) data can't bleed across the switch,
  // then publish the new user.
  async function swapSession(res: { token?: string; user: User }) {
    if (res.token) setToken(res.token);
    qc.removeQueries();
    let userToPublish: User = res.user;
    try {
      const meRes = await api.get<{ user: User }>('/api/auth/me');
      if (meRes?.user) userToPublish = meRes.user;
    } catch { /* fall back to the response user */ }
    setUser(userToPublish);
  }

  async function impersonate(target: { userId?: string; employeeCode?: string }) {
    const res = await api.post<{ token: string; user: User }>('/api/auth/impersonate', target);
    await swapSession(res);
  }

  async function stopImpersonating() {
    const res = await api.post<{ token: string; user: User }>('/api/auth/impersonate/exit', {});
    await swapSession(res);
  }

  return <Ctx.Provider value={{ user, loading, login, logout, impersonate, stopImpersonating }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be inside AuthProvider');
  return ctx;
}
